import { BusinessType } from "../legacy/types/legacyBusiness.types";
import type { BaseBusiness, Business } from "../legacy/types/legacyBusiness.types";
import { Organization, OrganizationKind, OrganizationSection } from "../domain/organizations/organization.types";
import { Product, ProductVariant } from "../domain/products/product.types";
import { ServiceCategory } from "../domain/services/service.types";
import type { Service, ServiceStatus } from "../domain/services/service.types";
import { Asset, VideoAsset } from "../domain/shared/asset.types";
import { ContactModule, SocialLinks } from "../domain/shared/contact.types";
import { LocalizedText } from "../domain/shared/localized-text.types";
import { LocationModule } from "../domain/shared/location.types";
import { Tour, TourStatus } from "../domain/tourism/tour.types";
import { buildServiceActionMessage } from "../domain/shared/commerce.utils";

type LocalizedValue = string | LocalizedText;

export interface LegacyProductContent {
  name: LocalizedValue;
  description?: LocalizedValue;
}

export interface LegacyTourContent {
  name: LocalizedValue;
  description: LocalizedValue;
  status?: LocalizedValue;
}

export interface LegacyVideoContent {
  name: LocalizedValue;
  description?: LocalizedValue;
}

export interface LegacyServiceContent {
  id: string;
  slug: string;
  name: LocalizedText;
  description?: LocalizedText;
  image?: string | string[];
  category: ServiceCategory;
  price?: LocalizedText;
  availability?: LocalizedText;
  status: ServiceStatus;
  featured?: boolean;
  order?: number;
  actionIntent?: "inquire" | "reserve" | "request-information" | "request-quote" | "contact";
  actionLabel?: LocalizedText;
}

export interface LegacyBusinessAdapterOptions {
  name?: LocalizedText;
  summary?: LocalizedText;
  description?: LocalizedText;
  products?: Record<string, LegacyProductContent>;
  tours?: Record<string, LegacyTourContent>;
  videos?: Record<string, LegacyVideoContent>;
  services?: LegacyServiceContent[];
  kind?: OrganizationKind;
}

const nonEmpty = (value?: string): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

const localized = (value?: LocalizedValue): LocalizedText | undefined => {
  if (typeof value === "string") {
    const text = nonEmpty(value);
    return text ? { es: text } : undefined;
  }

  const es = nonEmpty(value?.es);
  if (!es) return undefined;
  const en = nonEmpty(value?.en);
  return en ? { es, en } : { es };
};

const getLocalizedBusinessName = (
  source: BaseBusiness | Business,
  options: LegacyBusinessAdapterOptions
): LocalizedText => {
  if (options.name?.es.trim()) return options.name;
  if ("name" in source) {
    const name = localized(source.name);
    if (name) return name;
  }

  throw new Error(`Missing localized name for legacy business "${source.id}"`);
};

const toSocialLinks = (source: BaseBusiness["socialMedia"]): SocialLinks | undefined => {
  const social: SocialLinks = {
    ...(nonEmpty(source.facebook) ? { facebook: nonEmpty(source.facebook) } : {}),
    ...(nonEmpty(source.instagram) ? { instagram: nonEmpty(source.instagram) } : {}),
    ...(nonEmpty(source.tiktok) ? { tiktok: nonEmpty(source.tiktok) } : {}),
    ...(nonEmpty(source.twitter) ? { twitter: nonEmpty(source.twitter) } : {}),
    ...(nonEmpty(source.whatsapp) ? { whatsapp: nonEmpty(source.whatsapp) } : {}),
    ...(nonEmpty(source.website) ? { website: nonEmpty(source.website) } : {}),
  };

  return Object.values(social).some(Boolean) ? social : undefined;
};

const toContact = (source: BaseBusiness): ContactModule | undefined => {
  const email = nonEmpty(source.contactInfo.email);
  const phone = nonEmpty(source.contactInfo.phone);
  const address = localized(source.contactInfo.address);
  const social = toSocialLinks(source.socialMedia);

  if (!email && !phone && !address && !social) return undefined;

  return {
    emails: email ? [email] : undefined,
    phones: phone ? [phone] : undefined,
    address,
    social,
  };
};

const toLocation = (source: BaseBusiness): LocationModule | undefined => {
  const address = localized(source.contactInfo.address);
  if (!address && !source.location) return undefined;

  return {
    address,
    coordinates: source.location
      ? { latitude: source.location.lat, longitude: source.location.lng }
      : undefined,
  };
};

const toAssets = (image: string | string[], alt: LocalizedText): Asset[] =>
  (Array.isArray(image) ? image : [image]).map((src) => ({ src, alt }));

const getProductContent = (
  source: BaseBusiness | Business,
  productId: string,
  options: LegacyBusinessAdapterOptions
): LegacyProductContent | undefined => {
  if ("name" in source) {
    const product = source.products.find((item) => item.id === productId);
    if (product && "name" in product) return product;
  }
  return options.products?.[productId];
};

const toProductVariant = (
  productId: string,
  presentation: { weight?: string; flavor?: string; price: string },
  index: number
): ProductVariant | undefined => {
  const label = nonEmpty(presentation.weight) ?? nonEmpty(presentation.flavor);
  if (!label) return undefined;

  return {
    id: `${productId}--presentation-${index + 1}`,
    name: { es: label },
    price: { label: { es: presentation.price } },
  };
};

const toProducts = (
  source: BaseBusiness | Business,
  options: LegacyBusinessAdapterOptions
): Product[] =>
  source.products.map((legacyProduct) => {
    const content = getProductContent(source, legacyProduct.id, options);
    const name = localized(content?.name);
    if (!name) {
      throw new Error(`Missing localized product name for "${legacyProduct.id}"`);
    }

    const variants = legacyProduct.presentations
      ?.map((presentation, index) => toProductVariant(legacyProduct.id, presentation, index))
      .filter((variant): variant is ProductVariant => Boolean(variant));

    return {
      id: legacyProduct.id,
      name,
      description: localized(content?.description),
      images: toAssets(legacyProduct.image, name),
      price: legacyProduct.price ? { label: { es: legacyProduct.price } } : undefined,
      variants: variants?.length ? variants : undefined,
    };
  });

const getTourContent = (
  source: BaseBusiness | Business,
  tourId: string,
  options: LegacyBusinessAdapterOptions
): LegacyTourContent | undefined => {
  if ("name" in source) {
    const tour = source.tours?.find((item) => item.id === tourId);
    if (tour && "name" in tour) return tour;
  }
  return options.tours?.[tourId];
};

const toTourStatus = (status?: string): TourStatus => {
  if (status && /no disponible|unavailable/i.test(status)) return "temporarily-unavailable";
  return "available";
};

const toTours = (
  source: BaseBusiness | Business,
  options: LegacyBusinessAdapterOptions,
  contact: ContactModule | undefined
): Tour[] =>
  (source.tours ?? [])
    .filter((legacyTour) => !options.services?.some(({ id }) => id === legacyTour.id))
    .map((legacyTour) => {
    const content = getTourContent(source, legacyTour.id, options);
    const name = localized(content?.name);
    if (!name) {
      throw new Error(`Missing localized tour name for "${legacyTour.id}"`);
    }

    const description = localized(content?.description);
    if (!description) {
      throw new Error(`Missing localized tour description for "${legacyTour.id}"`);
    }
    const whatsapp = contact?.social?.whatsapp;

    return {
      id: legacyTour.id,
      name,
      description,
      images: toAssets(legacyTour.image, name),
      durationLabel: localized(legacyTour.duration),
      price: { label: { es: legacyTour.price } },
      reservation: whatsapp
        ? {
            method: "whatsapp",
            target: whatsapp,
            requiresDate: true,
            requiresPartySize: true,
          }
        : undefined,
      availability: localized(content?.status),
      status: toTourStatus(
        typeof content?.status === "string"
          ? content.status
          : [content?.status?.es, content?.status?.en].filter(Boolean).join(" ")
      ),
      classification: legacyTour.id === "charlas-apicultura" ? "ambiguous" : "tour",
    };
  });

const toServices = (
  options: LegacyBusinessAdapterOptions,
  contact: ContactModule | undefined,
  organizationName: LocalizedText
): Service[] => {
  const whatsapp = contact?.social?.whatsapp;

  return (options.services ?? []).map((service) => ({
    id: service.id,
    slug: service.slug,
    name: service.name,
    description: service.description,
    images: service.image ? toAssets(service.image, service.name) : undefined,
    category: service.category,
    price: service.price ? { label: service.price } : undefined,
    availability: service.availability,
    status: service.status,
    featured: service.featured,
    primaryAction: whatsapp
      ? {
          method: "whatsapp",
          target: whatsapp,
          intent: service.actionIntent ?? "inquire",
          label: service.actionLabel,
          messageTemplate: {
            es: buildServiceActionMessage({
              serviceName: service.name.es,
              organizationName: organizationName.es,
              intent: service.actionIntent,
              language: "es",
            }),
            ...(service.name.en && organizationName.en
              ? {
                  en: buildServiceActionMessage({
                    serviceName: service.name.en,
                    organizationName: organizationName.en,
                    intent: service.actionIntent,
                    language: "en",
                  }),
                }
              : {}),
          },
        }
      : undefined,
    reservation:
      whatsapp && service.actionIntent === "reserve"
        ? { method: "whatsapp", target: whatsapp, requiresDate: true }
        : undefined,
    order: service.order,
  }));
};

const toMedia = (
  source: BaseBusiness | Business,
  organizationName: LocalizedText,
  options: LegacyBusinessAdapterOptions
): { images?: Asset[]; videos?: VideoAsset[] } | undefined => {
  const images = source.images.map((image) => ({
    src: image.url,
    alt: localized(image.name) ?? organizationName,
  }));

  const videos = source.videos?.map((video) => {
    const localizedVideo = "name" in video ? video : options.videos?.[video.id];
    const title = localized(localizedVideo?.name) ?? organizationName;

    return {
      src: video.url,
      title,
      description: localized(localizedVideo?.description),
      thumbnail: video.thumbnail ? { src: video.thumbnail, alt: title } : undefined,
    };
  });

  if (!images.length && !videos?.length) return undefined;
  return { images: images.length ? images : undefined, videos: videos?.length ? videos : undefined };
};

const inferKind = (productCount: number, tourCount: number): OrganizationKind => {
  if (productCount > 0 && tourCount > 0) return "hybrid";
  if (tourCount > 0) return "tour-operator";
  return "product-business";
};

const buildSections = (organization: Omit<Organization, "sections">): OrganizationSection[] => {
  const sections: OrganizationSection[] = [{ type: "about", enabled: true }];
  if (organization.productCatalog?.products.length) sections.push({ type: "products", enabled: true });
  if (organization.serviceCatalog?.services.length) sections.push({ type: "services", enabled: true });
  if (organization.tourism?.tours.length) sections.push({ type: "tours", enabled: true });
  if (organization.media?.images?.length || organization.media?.videos?.length) {
    sections.push({ type: "gallery", enabled: true });
  }
  if (organization.contact) sections.push({ type: "contact", enabled: true });
  if (organization.location?.coordinates) sections.push({ type: "map", enabled: true });
  return sections;
};

export const adaptLegacyBusiness = (
  source: BaseBusiness | Business,
  options: LegacyBusinessAdapterOptions = {}
): Organization => {
  const name = getLocalizedBusinessName(source, options);
  const contact = toContact(source);
  const location = toLocation(source);
  const products = toProducts(source, options);
  const services = toServices(options, contact, name);
  const tours = toTours(source, options, contact);
  const media = toMedia(source, name, options);
  const whatsapp = contact?.social?.whatsapp;

  const organizationWithoutSections: Omit<Organization, "sections"> = {
    id: source.id,
    kind: options.kind ?? inferKind(products.length, tours.length),
    identity: {
      name,
      summary: options.summary ?? ("shortDescription" in source ? localized(source.shortDescription) : undefined),
      description:
        options.description ?? ("longDescription" in source ? localized(source.longDescription) : undefined),
      logo: { src: source.logo, alt: name },
      heroImage: source.backgroundImage ? { src: source.backgroundImage, alt: name } : undefined,
    },
    contact,
    location,
    productCatalog: products.length
      ? {
          products,
          ordering: whatsapp ? { method: "whatsapp", target: whatsapp } : undefined,
        }
      : undefined,
    serviceCatalog: services.length ? { services } : undefined,
    tourism: tours.length ? { tours } : undefined,
    media,
  };

  return {
    ...organizationWithoutSections,
    sections: buildSections(organizationWithoutSections),
  };
};

// Kept exported for consumers that need to document the legacy category during migration.
export type LegacyBusinessCategory = BusinessType;
